import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import {
  ADD_STEPS_CLEAR_STATE,
  FETCH_PREDICTED_STEPS,
  FETCH_PREDICTED_STEPS_EXAMPLES_FAILURE,
  FETCH_PREDICTED_STEPS_EXAMPLES_SUCCESS,
  FETCH_PREDICTED_STEPS_FAILURE,
  FETCH_PREDICTED_STEPS_SUCCESS,
  FETCH_PUBLISHED_STEPS,
  FETCH_PUBLISHED_STEPS_FAILURE,
  FETCH_PUBLISHED_STEPS_SUCCESS,
  FETCH_IN_HOUSE_STEPS_BY_DATABASE,
  FETCH_IN_HOUSE_STEPS_BY_DATABASE_SUCCESS,
  FETCH_IN_HOUSE_STEPS_BY_DATABASE_FAILURE,
  HIDE_ADD_STEPS_DIALOG,
  SHOW_ADD_STEPS_DIALOG,
  SET_SELECTED_INDEXES_BY_TAB,
  ADD_STEPS,
  ADD_STEPS_SUCCESS,
  ADD_STEPS_FAILURE,
  ErrorNumbers,
} from '@Pages/RetroSynthesis/EditPlan/AddSteps/constants';
import debounce from 'lodash/debounce';
import UserDatabases from '@Services/UserDatabases';
import map from 'lodash/map';
import { clearRateLimitedMultiStepReactionPreviewQueue } from '@Pages/RetroSynthesis/MultiStepReactionDialog/MultiStepReactionService';
import { runSearch } from '@Pages/RetroSynthesis/ProjectRoute/ReaxysExamplesSearchService';
import { buildRequestPayloadForExamplesSearch } from '@Pages/RetroSynthesis/ProjectRoute/utils/conditionsDataBuilder';
import { projectRouteDataCacheKey, STEP_DATA_EXAMPLES_SEARCH_DEBOUNCE_DELAY } from '@Pages/RetroSynthesis/ProjectRoute/constants';
import { noCache } from '@Utils/Network';
import mapPublishedResponse from '@Pages/RetroSynthesis/EditPlan/AddSteps/utils/mapPublishedResponse';
import { fetchProjectBreadcrumbInfoAction } from '@Pages/RetroSynthesis/RsBreadcrumbs/actions';
import namespacedCache from '@Services/NamespacedCache';
import { projectRoutesCacheKey } from '@Pages/RetroSynthesis/ProjectRoutes/constants';
import { showInfoToastMessage } from '@Containers/ToastMessages/InfoToastMessage/InfoToastMessageActions';
import mapPredictedResponse from '@Pages/RetroSynthesis/EditPlan/AddSteps/utils/mapPredictedResponse';
import { onAddSteps } from '@Pages/RetroSynthesis/EditPlan/AddSteps/analytics';
import { RouteType, ProjectSynthesisPartner } from '@Pages/RetroSynthesis/constants';
import { RCSANY } from '../../ProjectParametersDialog/constants';

let fetchPredictedStepsCancel;
export const showAddStepsDialog = (selectedStep, cleanState = true) => ({
  payload: { selectedStep, cleanState },
  type: SHOW_ADD_STEPS_DIALOG,
});
export const hideAddStepsDialog = (cancelCurrentSearch = true) => {
  // cancel previous search if running
  if (cancelCurrentSearch) {
    // predicted steps
    if (fetchPredictedStepsCancel) {
      fetchPredictedStepsCancel();
      fetchPredictedStepsCancel = undefined;
    }
  }
  return { type: HIDE_ADD_STEPS_DIALOG };
};
export const cleanState = () => ({ type: ADD_STEPS_CLEAR_STATE });

export const fetchPublishedSteps = (step, params) => {
  return async (dispatch) => {
    dispatch({ type: FETCH_PUBLISHED_STEPS });
    try {
      const payload = {
        structure: step.substance.structure,
        databases: [UserDatabases.getDefaultDatabase().id],
        ...params,
      };
      const response = await MessageBroker.send(RequestUrls.rsProjectsPublished, payload);
      dispatch({
        type: FETCH_PUBLISHED_STEPS_SUCCESS,
        payload: mapPublishedResponse(response),
      });
    } catch (error) {
      dispatch({ type: FETCH_PUBLISHED_STEPS_FAILURE, payload: error });
    }
  };
};

export const fetchInHouseByDatabase = (step, databaseId, params) => {
  return async (dispatch) => {
    dispatch({ type: FETCH_IN_HOUSE_STEPS_BY_DATABASE, payload: { databaseId } });
    try {
      const payload = {
        structure: step.substance.structure,
        databases: [databaseId],
        ...params,
      };
      const response = await MessageBroker.send(RequestUrls.rsProjectsPublished, payload);
      dispatch({
        type: FETCH_IN_HOUSE_STEPS_BY_DATABASE_SUCCESS,
        payload: {
          databaseId,
          response: mapPublishedResponse(response),
        },
      });
    } catch (error) {
      dispatch({ type: FETCH_IN_HOUSE_STEPS_BY_DATABASE_FAILURE, payload: { databaseId, error } });
    }
  };
};

export const fetchInHouseSteps = (step, databaseIds = []) => {
  return (dispatch) => {
    databaseIds.forEach((databaseId) => {
      dispatch(fetchInHouseByDatabase(step, databaseId));
    });
  };
};

export const fetchPredictedSteps = (step, projectId, partner, params) => {
  return async (dispatch) => {
    dispatch({ type: FETCH_PREDICTED_STEPS });
    const predictedParams =
      partner === ProjectSynthesisPartner.PAI
        ? {
            numberOfPlans: '10',
            identicalReactionsSteps: '3',
            identicalBuildingBlocks: '3',
            fullSynthesisPlan: false, // only this one is matter
            processingTime: 10,
            usedBuildingBlocks: [],
            partner,
          }
        : {
            maxNoOfSteps: 1,
            selectivity: false,
            usedBuildingBlocks: [RCSANY],
            processingTime: 2,
            partner,
          };
    const payload = {
      structure: step.substance.structure,
      predictedParams,
      projectId,
      ...params,
    };
    const promise = MessageBroker.send(RequestUrls.rsProjectsPredicted, payload);
    fetchPredictedStepsCancel = () => {
      promise.cancel();
      MessageBroker.send(RequestUrls.rsProjectsPredictedCancel, { projectId });
    };
    return promise
      .then((response) => {
        dispatch({ type: FETCH_PREDICTED_STEPS_SUCCESS, payload: mapPredictedResponse(response) });
        fetchPredictedStepsCancel = undefined;
      })
      .catch((error) => {
        const { errorNumber, message } = error || {};
        dispatch({ type: FETCH_PREDICTED_STEPS_FAILURE, payload: { error, response: mapPredictedResponse({}) } });
        if (errorNumber === ErrorNumbers.PREDICTED_NOT_SUPPORTED_FOR_STRUCTURE) {
          showInfoToastMessage({
            content: message,
          });
        }
      });
  };
};

let examplesDataSearchCancel;
const fetchExamplesDataDebounced = debounce(
  async (step, items, dispatch) => {
    try {
      const databaseId = UserDatabases.getDefaultDatabase().id;
      const substances = map(items, ({ routeLayout }) => routeLayout[0]); // first substance
      const reactionIds = map(substances, ({ examples }) => examples[0].reactionId); // first example
      // run a new search
      const { promise: searchPromise, cancel: searchCancel } = runSearch(
        buildRequestPayloadForExamplesSearch({
          reactionIds,
          databaseId,
        })
      );

      // save cancel callback
      examplesDataSearchCancel = searchCancel;

      // await for search result
      const resultItem = await searchPromise;

      // if everything ok - remove cancel callback
      examplesDataSearchCancel = undefined;
      dispatch({
        type: FETCH_PREDICTED_STEPS_EXAMPLES_SUCCESS,
        payload: { step, data: resultItem },
      });
    } catch (error) {
      // if something went wrong - cancel search
      // eslint-disable-next-line no-unused-expressions
      examplesDataSearchCancel && examplesDataSearchCancel();

      dispatch({
        type: FETCH_PREDICTED_STEPS_EXAMPLES_FAILURE,
        payload: { step, error },
      });
    }
  },
  STEP_DATA_EXAMPLES_SEARCH_DEBOUNCE_DELAY,
  { leading: true, trailing: true }
);

export const fetchExamplesData = (step, items) => {
  return async (dispatch) => {
    // cancel previous search if running
    if (examplesDataSearchCancel) {
      examplesDataSearchCancel();
      examplesDataSearchCancel = undefined;
    }

    fetchExamplesDataDebounced(step, items, dispatch);
  };
};

export const setSelectedItemsByTab = (tab, selectedItems) => ({
  type: SET_SELECTED_INDEXES_BY_TAB,
  payload: {
    tab,
    selectedItems,
  },
});

export const addRouteSteps = (projectId, routeId, steps, trackingData) => {
  return async (dispatch) => {
    // Cancel pending multi-step preview requests
    clearRateLimitedMultiStepReactionPreviewQueue();

    dispatch({ type: ADD_STEPS });
    try {
      const response = await MessageBroker.send(RequestUrls.rsProjectsAddRouteSteps, { projectId, routeId }, noCache(), steps);
      dispatch({ type: ADD_STEPS_SUCCESS, payload: response });
      // AA
      const { publishedCount, predictedCount, inHouseCount, projectInfo } = trackingData;
      onAddSteps({
        projectId,
        routeId: response,
        routeType: RouteType.CUSTOMIZED,
        publishedCount,
        predictedCount,
        inHouseCount,
        projectInfo,
      });

      namespacedCache.removeByKey(projectRouteDataCacheKey(projectId, routeId));
      namespacedCache.removeByKey(projectRoutesCacheKey(projectId));
      // Refresh customizedRoutesCount in RsBreadcrumbs
      dispatch(fetchProjectBreadcrumbInfoAction(projectId));
    } catch (error) {
      dispatch({ type: ADD_STEPS_FAILURE, payload: error });
    }
  };
};
